import React, { useState, useRef, useEffect, useMemo } from 'react'
import {
  encryptStr,
  getSearch,
  loginByCode,
  VERIFIER_CODE,
  VERIFIER_CODE_ZH,
  ERROR_CODE,
  isInStudent,
  cleanLoginStorage,
} from '@yuanfudao/metis-unified-login'
import { sendVerifyCode } from '@yuanfudao/metis-unified-login/es/service/login'
import { useGlobalState } from '@/store'
import { formatCode, formatPhone, onNumberInputChange, phoneReg } from '@yuanfudao/metis-unified-login/es/utils/phone'
import icLoading from '@/assets/login/loading.png'

import { useHistory } from 'react-router-dom'
import { resolveRoute } from '@/routes/routes'
import { EMBED_TYPE } from '@/pages/embed'
import { useHandleQuery } from '@/hooks/common'
import lessModule from './index.module.less'
import { LOGIN_TYPE } from '../../index'
import { ProjectConfig } from '@/project-config'
import type { PasswordLoginResult, ResetPasswordSession } from '@/service/password-login'
import { metisClog } from '@/metis-clog'
import { useMsgFail } from '@yuanfudao/metis-unified-login/es/utils/antd-message'

interface CodeContentProps{
  loginType: LOGIN_TYPE,
  isCheckAgreement?: boolean,
  handleChangeLoginType: (type:LOGIN_TYPE)=>void,
  afterLoginSuccess: ()=>void,
  onNotAgree: () => void,
  onInitialPassword: (session: ResetPasswordSession) => void,
}

const canSee = 'https://metis-online.fbcontent.cn/metis-lectio/47lCfBsHMspk3AuCjnPpBh.png'
const canNotSee = 'https://metis-online.fbcontent.cn/metis-lectio/6cVQw6ekvIP3QRsh0Pyjmt.png'

export const CodeContent = ({ loginType, isCheckAgreement, handleChangeLoginType, afterLoginSuccess, onNotAgree, onInitialPassword }:CodeContentProps) => {
  const { getQuery } = useHandleQuery()
  const [loginActive, setLoginActive] = useState(false)
  const [phone, setPhone] = useState('')
  const [account, setAccount] = useState('')
  const [identifyCode, setIdentifyCode] = useState('')
  const [password, setPassword] = useState('')
  const [codeText, setCodeText] = useState('获取验证码')
  const [sendDisabled, setSendDisabled] = useState(false)
  const timer = useRef(0)
  const [phoneErrText, setPhoneErrText] = useState('')
  const [codeErrText, setCodeErrText] = useState('')
  const [loginErrText, setLoginErrText] = useState('')
  const [phoneAutoFill, setPhoneAutoFill] = useState(false)
  const [canSeePsd, setCanSeePsd] = useState(false)
  const phoneRef = useRef<HTMLInputElement>(null)
  const codeRef = useRef<HTMLInputElement>(null)
  const psdRef = useRef<HTMLInputElement>(null)
  const removePasswordTimeout = useRef(0)

  const { setIsLogin } = useGlobalState()
  const MsgFail = useMsgFail()

  const isPhoneValid = useMemo(() => phoneReg.test(phone), [phone])
  const isCodeValid = identifyCode.length === 6
  const canLogin = loginType === LOGIN_TYPE.CODE
    ? isCodeValid && (isPhoneValid || phoneAutoFill)
    : account.trim().length > 0 && password.length > 0

  const history = useHistory()

  const resetCode = () => {
    clearInterval(timer.current)
    setSendDisabled(false)
    setCodeText('重新获取')
  }

  const clearPasswordValueAttribute = () => {
    // https://github.com/ant-design/ant-design/issues/20541
    removePasswordTimeout.current = window.setTimeout(() => {
      if (psdRef.current && psdRef.current.hasAttribute('value')) {
        psdRef.current.removeAttribute('value')
      }
    })
  }

  const handleSendCode = async () => {
    if (sendDisabled || !isPhoneValid) {
      return
    }
    try {
      const encrypPhone = encryptStr(phone) as string
      await sendVerifyCode({ phone: encrypPhone })
      setSendDisabled(true)
      let seconds = 60
      setCodeText(`${seconds} 秒后重试`)
      timer.current = window.setInterval(() => {
        seconds--
        setCodeText(`${seconds} 秒后重试`)
        if (seconds <= 0) {
          resetCode()
        }
      }, 1000)
      setCodeErrText('')
    } catch (e: any) {
      resetCode()
      console.warn(e?.response)
      let text = ''
      if (e?.response?.status) {
        text = VERIFIER_CODE_ZH[e?.response?.status as VERIFIER_CODE]
      } else {
        text = '获取验证码失败，请稍后重试'
      }
      setCodeErrText(text)
    }
  }

  const onLoginErr = (code: ERROR_CODE, msg?: string) => {
    setLoginActive(false)
    let text = msg
    const redirect = (getQuery().redirectUrl as string) || ''
    if (text) {
      if (redirect.includes('xuesheng') && [ERROR_CODE.LOGIN_FAIL_OVER_FREQ, ERROR_CODE.PHONE_FAIL_OVER_FREQ].includes(code)) {
        setCodeErrText('已验证失败多次，请于10分钟后重试，或找老师解锁账号')
      } else {
        // text = ERROR_CODE_ZH[code as ERROR_CODE]
        setCodeErrText(text)
      }
    } else {
      text = '网络错误，登录失败，请重试'
      setLoginErrText(text)
    }
  }

  const handleLogin = async () => {
    if (canLogin && !loginActive) {
      try {
        if (!isCheckAgreement) {
          // setLoginErrText('请先阅读并勾选底部的协议')
          MsgFail('请先阅读并勾选底部的协议')
          onNotAgree()
          return
        }
        setCodeErrText('')
        setLoginErrText('')
        setLoginActive(true)
        const loginId = loginType === LOGIN_TYPE.CODE ? phone : account.trim()
        const encrypPhone = encryptStr(loginId) as string
        if (loginType === LOGIN_TYPE.CODE) {
          // let auto = !isMetisTeacher && !studentReg.test(decodeURIComponent(redirect))
          const auto = isInStudent()
          console.log('auto', auto)
          const encrypCode = encryptStr(identifyCode) as string
          const { data: { code, message } } = await loginByCode({
            phone: encrypPhone,
            verification: encrypCode,
            autoRegister: auto,
          })
          if (code !== '0') {
            onLoginErr(code as ERROR_CODE, message)
            return
          }
        } else {
          const encrypPsw = encryptStr(password) as string
          const { data: { code, message, result } } = await ProjectConfig.loginLeft.passwordLoginFn({
            phone: encrypPhone,
            password: encrypPsw,
          })
          if (code !== '0') {
            onLoginErr(code as ERROR_CODE, message)
            return
          }
          const loginResult = result as PasswordLoginResult
          if (loginResult.needResetPassword) {
            cleanLoginStorage()
            setIsLogin(false)
            setLoginActive(false)
            if (!loginResult.resetPasswordTicket || !loginResult.resetPasswordPhone) {
              setCodeErrText('初始密码登录需要设置新密码，暂未获取到改密凭证，请重试或使用忘记密码')
              return
            }
            onInitialPassword({
              phone: loginResult.resetPasswordPhone,
              ticket: loginResult.resetPasswordTicket,
              loginId,
              expireAt: loginResult.resetPasswordExpireAt,
            })
            return
          }
        }
        setLoginActive(false)
        setIsLogin(true)
        afterLoginSuccess()
      } catch (e: any) {
        console.warn(e)
        onLoginErr((e?.code || '-1') as ERROR_CODE, e?.response?.data?.message || e?.message)
      }
    }
  }

  // const changeLoginType = () => {
  //   const nextType = loginType === LOGIN_TYPE.CODE ? LOGIN_TYPE.PASSWORD : LOGIN_TYPE.CODE
  //   handleChangeLoginType(nextType)
  //   setCodeErrText('')
  //   setLoginErrText('')
  //   if (nextType === LOGIN_TYPE.CODE) {
  //     setPsdAutoFill(false)
  //   }
  //   localStorage.setItem(loginTypeStorageKey, nextType)
  // }

  const onPhoneBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const p = e.target.value.replace(/[^0-9]/g, '')
    if (p.length > 0 && !phoneReg.test(p)) {
      setPhoneErrText('手机号格式错误')
    } else {
      setPhoneErrText('')
    }
  }

  const goToResetPsd = () => {
    history.push(`${resolveRoute({ name: 'embed', params: { type: EMBED_TYPE.RESET_PASSWORD } })}${getSearch()}`)
  }

  useEffect(() => {
    setCodeErrText('')
    setLoginErrText('')
    setPhoneErrText('')
    setPhoneAutoFill(false)
  }, [loginType])

  useEffect(() => {
    clearPasswordValueAttribute()
  })

  useEffect(() => () => {
    clearInterval(timer.current)
    clearTimeout(removePasswordTimeout.current)
  }, [])

  useEffect(() => {
    if (isPhoneValid) {
      metisClog.send('/login/phoneValid', { phone })
    }
  }, [isPhoneValid, phone])

  return (
    <div
      className={lessModule.contentWrap}
      onKeyPress={(e) => {
        if (e.key === 'Enter') {
          handleLogin()
        }
      }}
    >

      {/* 手机号或账号 */}
      <div
        className={lessModule.inputWrap}
      >
        <input
          className={lessModule.phone}
          ref={phoneRef}
          type="text"
          autoComplete={loginType === LOGIN_TYPE.CODE ? 'tel' : 'username'}
          name={loginType === LOGIN_TYPE.CODE ? 'tel' : 'username'}
          placeholder={loginType === LOGIN_TYPE.CODE ? '请输入手机号' : '请输入手机号或账号'}
          value={loginType === LOGIN_TYPE.CODE ? formatPhone(phone) : account}
          onChange={(e) => {
            if (loginType === LOGIN_TYPE.CODE) {
              onNumberInputChange(e, 11, setPhone)
            } else {
              setAccount(e.target.value)
            }
          }}
          onBlur={loginType === LOGIN_TYPE.CODE ? onPhoneBlur : undefined}
          onAnimationStart={(e) => setPhoneAutoFill(!e.animationName.includes('noopOut'))}
        />
        <div className={lessModule.textError}>{phoneErrText ? `* ${phoneErrText}` : ''}</div>
      </div>

      {/* 密码｜验证码 */}
      {
        loginType === LOGIN_TYPE.CODE ? (
          <div
            className={lessModule.inputWrap}
          >
            <input
              className={lessModule.code}
              type="text"
              ref={codeRef}
              placeholder="请输入验证码"
              value={formatCode(identifyCode)}
              onChange={(e) => {
                onNumberInputChange(e, 6, setIdentifyCode)
              }}
            />
            <span
              style={sendDisabled ? { cursor: 'not-allowed' } : {}}
              className={`${lessModule.sendCode} ${(isPhoneValid && !sendDisabled) ? lessModule.active : ''}`}
              onClick={handleSendCode}
            >{codeText}</span>
            <div className={lessModule.textError}>{codeErrText ? `${codeErrText}` : ''}</div>
          </div>
        ) : (
          <div
            className={lessModule.inputWrap}
          >
            <input
              className={lessModule.password}
              type={canSeePsd ? 'text' : 'password'}
              autoComplete="current-password"
              name="current-password"
              ref={psdRef}
              placeholder="请输入密码"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={clearPasswordValueAttribute}
              onBlur={clearPasswordValueAttribute}
            />
            <img
              alt=""
              src={canSeePsd ? canSee : canNotSee}
              className={lessModule.eye}
              onClick={() => setCanSeePsd(!canSeePsd)}
            />
            <div className={lessModule.textError}>{codeErrText}</div>
          </div>
        )
      }

      <div className={`${lessModule.inputWrap} ${lessModule.loginBtnWrap}`}>
        <button
          type="button"
          className={`${lessModule.loginBtn} ${canLogin ? lessModule.active : ''}`}
          style={loginActive ? { cursor: 'not-allowed' } : {}}
          onClick={handleLogin}
        >
          {
            loginActive ? (
              <>
                <img className={lessModule.loading} src={icLoading} alt="" />
                登录中...
              </>
            ) : '登录'
          }
        </button>
        <div className={lessModule.textError}>{loginErrText}</div>
      </div>

      {/* <div onClick={changeLoginType} className={[lessModule.toggle, loginType === LOGIN_TYPE.PASSWORD ? lessModule.toggleToCode : ''].join(' ')}>
        <div className={lessModule.text}>
          <div className={`${lessModule.toggleIcon} ${loginType === LOGIN_TYPE.CODE ? lessModule.codeToggle : lessModule.passwordToggle}`} />
          {
            loginType === LOGIN_TYPE.CODE ? '切换密码登录' : '切换验证码登录'
          }
        </div>
        {
          loginType === LOGIN_TYPE.PASSWORD && (
            <div className={lessModule.forget} onClick={goToResetPsd}>忘记密码?</div>
          )
        }
      </div> */}
      {
        loginType === LOGIN_TYPE.PASSWORD && ProjectConfig.loginLeft.ResetPswComponent && (
          // eslint-disable-next-line react/jsx-pascal-case
          <ProjectConfig.loginLeft.ResetPswComponent
            onClick={goToResetPsd}
          />
        )
      }
    </div>
  )
}
